// @givmo/app-admin — employer gift-card BATCH REPORT (EP-WEB5).
//
// The Batches tab of the /gift-cards page: a paginated LIST of the company's bulk-gift batches
// (`GET /admin/company/gift-cards/batches`) drilling into a per-batch DETAIL
// (`GET /admin/company/gift-cards/batches/{id}`) — the aggregated per-status delivery counts, the
// D-EP-16(d) delivery-failure warning (never silent), and the per-card rows (reusing the shared
// `GiftCardStatusCell`, so redemption rendering stays gated in ONE place). Read-only: no money moves
// here (the batch is minted by the confirm dialog); this is where the admin watches delivery land.
//
// ★ The warning fires on `delivery_counts.failed` — EVERY failed delivery, not just the
// `enqueue_failed` subset (EP-FIX13). The two failure kinds differ only in internals (a card that
// never enqueued lost its code at mint time; a retry-exhausted one exhausted the worker's attempts),
// and the wire keeps `enqueue_failed` ⊆ `failed` — but the employer's position is identical either
// way: the recipient has nothing, and recovery for BOTH is the same support-driven super-admin
// void+reissue (its selector matches FAILED-and-still-ACTIVE, both kinds alike). So this renders one
// honest banner rather than a split that would imply two different things to do. Keying it off
// `enqueue_failed` alone was the trap: a batch whose deliveries died in the worker showed a red
// "Failed" tile with NO warning beside it.

import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import {
  sdk,
  isGivmoError,
  type CompanyGiftCardBatchOut,
  type CompanyGiftCardOut,
} from '@givmo/api-client';
import { toDateOnly } from '@givmo/api-client/list';
import { companiesGetCompanyGiftCardBatchOptions } from '@givmo/api-client/query';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  Icon,
  Skeleton,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { AlertTriangle, ArrowLeft, Package } from 'lucide-react';
import { fundingLabel, formatGiftCardAmount, formatGiftCardCharged } from './gift-cards-decode';
import {
  batchStatusLabel,
  extractCompanyGiftCardBatches,
  formatBatchTotalFromWire,
} from './gift-cards-batch-decode';
import { GiftCardStatusCell } from './gift-card-status-cell';

const BATCHES_PAGE_LIMIT = 10;
const BATCHES_LIST_KEY = ['companies-list_company_gift_card_batches', 'report'] as const;

async function fetchBatchesPage(
  offset: number,
  signal: AbortSignal,
): Promise<CompanyGiftCardBatchOut[]> {
  const { data } = await sdk.companiesListCompanyGiftCardBatches({
    query: { 'page[limit]': BATCHES_PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractCompanyGiftCardBatches(data);
}

export interface BatchReportProps {
  /** Opens the bulk-compose flow from the empty state ("Send to multiple people"). */
  onComposeCta: () => void;
  /**
   * A batch to open directly, set when another surface hands off to this one (an occasion execution
   * links to the batch that paid for it, EP-BE8-web). It SEEDS the view for that one hand-off — see
   * `onFocusConsumed`. Omit for the ordinary self-contained list → detail behaviour.
   */
  focusBatchId?: string | null;
  /**
   * Called once `focusBatchId` has been applied, so the OWNER can clear it.
   *
   * ★ Load-bearing, not bookkeeping. This component lives in a `Tabs.Panel`, which UNMOUNTS while its
   * tab is inactive — so an id left set is re-read by `useState` on the next mount, and every later
   * visit to this tab would silently re-open that stale batch instead of the list, for the rest of the
   * session. Clearing on consumption is what keeps the hand-off a one-shot.
   */
  onFocusConsumed?: () => void;
}

export function BatchReport({
  onComposeCta,
  focusBatchId = null,
  onFocusConsumed,
}: BatchReportProps) {
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(focusBatchId);

  // Follow a hand-off, then immediately tell the owner it has been consumed (see `onFocusConsumed` —
  // an uncleared id turns every later visit to this tab into the stale batch's detail).
  useEffect(() => {
    if (focusBatchId === null) return;
    setSelectedBatchId(focusBatchId);
    onFocusConsumed?.();
  }, [focusBatchId, onFocusConsumed]);

  if (selectedBatchId !== null) {
    return <BatchDetailView batchId={selectedBatchId} onBack={() => setSelectedBatchId(null)} />;
  }
  return <BatchList onSelect={setSelectedBatchId} onComposeCta={onComposeCta} />;
}

// ── Batch list ────────────────────────────────────────────────────────────────────────────────────

function BatchList({
  onSelect,
  onComposeCta,
}: {
  onSelect: (batchId: string) => void;
  onComposeCta: () => void;
}) {
  const list = useInfiniteQuery({
    queryKey: BATCHES_LIST_KEY,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof BATCHES_LIST_KEY, number>) =>
      fetchBatchesPage(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < BATCHES_PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });

  const batches = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  const columns: DataTableColumn<CompanyGiftCardBatchOut>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Batch',
        width: '30%',
        cell: (b) => (
          <span className="truncate font-w600 text-text-strong">{b.name?.trim() || '—'}</span>
        ),
      },
      {
        id: 'recipients',
        header: 'Recipients',
        width: '110px',
        cell: (b) => <span className="tabular-nums text-text-strong">{b.quantity}</span>,
      },
      {
        id: 'total',
        header: 'Total face',
        width: '120px',
        cell: (b) => {
          // Batch face total + (card batch only) the exact GROSS charged from the response (EP-FIX3) —
          // null for a wallet batch → face only, never "$0".
          const charged = formatGiftCardCharged(b.amount_charged);
          return (
            <div className="flex flex-col">
              <span className="tabular-nums text-text-strong">
                {formatBatchTotalFromWire(b.denomination, b.quantity)}
              </span>
              {charged !== null && (
                <span
                  className="tabular-nums text-s12 text-text-tertiary"
                  {...testIdProp(`gift-card-batch-charged-${b.id}`)}
                >
                  Charged {charged}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'funding',
        header: 'Funding',
        width: '140px',
        cell: (b) => <span className="text-text-secondary">{fundingLabel(b.funding)}</span>,
      },
      {
        id: 'created',
        header: 'Created',
        width: '120px',
        cell: (b) => <span className="text-text-secondary">{toDateOnly(b.created_at) || '—'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        cell: (b) => (
          <Badge tone="neutral" dot testId={`gift-card-batch-status-${b.id}`}>
            {batchStatusLabel(b.status)}
          </Badge>
        ),
      },
    ],
    [],
  );

  return (
    <DataTable
      testId="gift-card-batches-table"
      columns={columns}
      rows={batches}
      rowKey={(b) => b.id}
      rowTestId={(b) => `gift-card-batch-row-${b.id}`}
      onRowClick={(b) => onSelect(b.id)}
      loading={list.isPending}
      error={
        list.isError
          ? isGivmoError(list.error)
            ? list.error.message
            : 'Failed to load batches.'
          : undefined
      }
      onRetry={() => void list.refetch()}
      emptyState={
        <EmptyState
          testId="gift-card-batches-empty-state"
          icon={<Icon icon={Package} size={20} aria-hidden />}
          title="No batches yet"
          description="Gift-card batches you send to multiple employees will appear here."
          action={
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              onClick={onComposeCta}
              testId="gift-card-batches-empty-compose"
            >
              Send to multiple people
            </Button>
          }
        />
      }
      footer={
        list.hasNextPage ? (
          <>
            <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
              Showing {batches.length} batches
            </span>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
              testId="gift-card-batches-load-more"
            >
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </>
        ) : undefined
      }
    />
  );
}

// ── Batch detail ──────────────────────────────────────────────────────────────────────────────────

// Module-level: these cells close over nothing from the component, so the array holds ONE identity
// for the app's lifetime and the memoized rows never re-render for a columns change. (It must not
// be a hook here at all — it would sit behind this view's pending/error early returns.)
const CARD_COLUMNS: DataTableColumn<CompanyGiftCardOut>[] = [
  {
    id: 'recipient',
    header: 'Recipient',
    width: '40%',
    cell: (card) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-w600 text-text-strong">
          {card.recipient_name?.trim() || card.recipient_email?.trim() || '—'}
        </span>
        {card.recipient_email && card.recipient_name?.trim() && (
          <span className="truncate text-s12 text-text-tertiary">{card.recipient_email}</span>
        )}
      </div>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    width: '120px',
    cell: (card) => (
      <span className="tabular-nums text-text-strong">{formatGiftCardAmount(card.amount)}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    width: '160px',
    cell: (card) => <GiftCardStatusCell card={card} />,
  },
];

function BatchDetailView({ batchId, onBack }: { batchId: string; onBack: () => void }) {
  const detail = useQuery({
    ...companiesGetCompanyGiftCardBatchOptions({ path: { batch_id: batchId } }),
    retry: false,
  });

  const back = (
    <Button
      appearance="saas"
      tone="ghost"
      size="sm"
      leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
      onClick={onBack}
      testId="gift-card-batch-detail-back"
    >
      Back to batches
    </Button>
  );

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-16" {...testIdProp('gift-card-batch-detail')}>
        {back}
        <Skeleton className="h-24 w-[240px] rounded-lg" />
        <Skeleton className="h-80 w-full rounded-lg" />
      </div>
    );
  }

  if (detail.isError || detail.data === undefined) {
    return (
      <div className="flex flex-col gap-16" {...testIdProp('gift-card-batch-detail')}>
        {back}
        <div
          className="flex flex-col items-center gap-8 py-24 text-center"
          {...testIdProp('gift-card-batch-detail-error')}
        >
          <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
            {isGivmoError(detail.error) ? detail.error.message : 'Failed to load this batch.'}
          </p>
          <button
            type="button"
            onClick={() => void detail.refetch()}
            className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const batch = detail.data;
  const counts = batch.delivery_counts;
  const deliveryFailed = counts.failed ?? 0;
  const cards: CompanyGiftCardOut[] = batch.cards ?? [];
  // The batch's own GROSS charged (card batch only) from the response (EP-FIX3) — null for a wallet
  // batch. The per-card rows leave their own `amount_charged` null (the batch is the charge grain).
  const batchCharged = formatGiftCardCharged(batch.amount_charged);

  return (
    <div className="flex flex-col gap-16" {...testIdProp('gift-card-batch-detail')}>
      {back}

      <Card>
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <h3 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              {batch.name?.trim() || 'Batch'}
            </h3>
            <Badge tone="neutral" dot testId="gift-card-batch-detail-status">
              {batchStatusLabel(batch.status)}
            </Badge>
          </div>
          <p className="font-sans text-s13 tracking-givmo text-text-secondary tabular-nums">
            {batch.quantity} recipient{batch.quantity === 1 ? '' : 's'} ·{' '}
            {formatBatchTotalFromWire(batch.denomination, batch.quantity)} total face ·{' '}
            {batchCharged !== null && (
              <span {...testIdProp('gift-card-batch-detail-charged')}>
                Charged {batchCharged} ·{' '}
              </span>
            )}
            {fundingLabel(batch.funding)} · {toDateOnly(batch.created_at) || '—'}
          </p>

          {/* ── Aggregated per-status delivery counts ──
              The SENT tile reads "Delivered", the word the per-row status cell uses for that same
              wire state — one batch, one vocabulary. */}
          <div className="flex flex-wrap gap-8" {...testIdProp('gift-card-batch-delivery-counts')}>
            <DeliveryStat
              label="Pending"
              value={counts.pending}
              tone="warning"
              testId="gift-card-batch-count-pending"
            />
            <DeliveryStat
              label="Delivered"
              value={counts.sent}
              tone="success"
              testId="gift-card-batch-count-sent"
            />
            <DeliveryStat
              label="Failed"
              value={counts.failed}
              tone="danger"
              testId="gift-card-batch-count-failed"
            />
          </div>

          {/* ── Delivery-failure warning (D-EP-16(d)) — never silent, see the header. ──
              The testid stays `…-enqueue-failed` although the trigger is now every failure: it is
              the pinned e2e/RTL contract, and renaming it buys nothing an admin can see. */}
          {deliveryFailed > 0 && (
            <div
              className="flex items-start gap-8 rounded-lg border border-danger-100 bg-danger-50 p-12"
              {...testIdProp('gift-card-batch-enqueue-failed')}
            >
              <Icon icon={AlertTriangle} size={16} aria-hidden className="mt-1 text-danger-700" />
              <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                {deliveryFailed} card{deliveryFailed === 1 ? '' : 's'} could not be delivered —
                contact support to void &amp; reissue {deliveryFailed === 1 ? 'it' : 'them'}.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <DataTable
        testId="gift-card-batch-cards-table"
        columns={CARD_COLUMNS}
        rows={cards}
        rowKey={(c) => c.id}
        rowTestId={(c) => `gift-card-batch-card-row-${c.id}`}
        emptyState={
          <EmptyState
            testId="gift-card-batch-cards-empty"
            icon={<Icon icon={Package} size={20} aria-hidden />}
            title="No cards in this batch"
            description="This batch has no gift cards to show."
          />
        }
      />
    </div>
  );
}

function DeliveryStat({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: number;
  tone: 'warning' | 'success' | 'danger';
  /** Passed explicitly, never derived from `label` — these ids are pinned by the e2e spec and the
   *  RTL suite, so the wording must stay free to change (it did: "Sent" → "Delivered") without a
   *  silent testid rename riding along. */
  testId: string;
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success-600'
      : tone === 'danger'
        ? 'text-danger-700'
        : 'text-warning-600';
  return (
    <div
      className="flex items-baseline gap-6 rounded-lg border border-border-subtle bg-surface-sunken px-12 py-8"
      {...testIdProp(testId)}
    >
      <span className={`font-sans text-s16 font-w700 tabular-nums ${toneClass}`}>{value}</span>
      <span className="font-sans text-s12 font-w500 tracking-givmo text-text-secondary">
        {label}
      </span>
    </div>
  );
}
